import { RUN_CONTRACTS } from '../utils/constants';
import type { RunContract, RunContractStats } from './runContracts';

export type RunGrade = 'SS' | 'S' | 'A' | 'B' | 'C';

export type RunGradeResult = {
  grade: RunGrade;
  score: number;
  label: string;
  detail: string;
};

export type RunGradeInput = RunContractStats & {
  maxSpeedKmh: number;
  nearMisses: number;
  closestCopDistance: number;
  completed: boolean;
  totalCollisions?: number;
};

function contractScore(contract: RunContract, input: RunGradeInput): number {
  switch (contract.kind) {
    case 'takedown':
      return Math.min(28, (input.takedowns / RUN_CONTRACTS.TAKEDOWN_TARGET) * 28);
    case 'chaos':
      return Math.min(28, (input.propertyDamage / RUN_CONTRACTS.CHAOS_BILL_TARGET) * 28);
    case 'breaker':
      return Math.min(28, (input.pursuitBreakers / RUN_CONTRACTS.PURSUIT_BREAKER_TARGET) * 28);
    case 'style':
      return Math.min(28, (input.stylePoints / RUN_CONTRACTS.STYLE_POINTS_TARGET) * 28);
    case 'rush':
      return input.elapsedS <= RUN_CONTRACTS.FAST_ESCAPE_TARGET_S
        ? 28
        : Math.max(8, 28 - (input.elapsedS - RUN_CONTRACTS.FAST_ESCAPE_TARGET_S) * 0.55);
    case 'escape':
    default:
      return 22;
  }
}

export function gradeRun(contract: RunContract, input: RunGradeInput): RunGradeResult {
  const speedScore = Math.min(20, input.maxSpeedKmh / 12);
  const hasModifier = (kind: string) => contract.modifiers.some((m) => m.kind === kind);
  const styleScore = Math.min(
    hasModifier('style_sponsor') ? 24 : 18,
    input.stylePoints / (hasModifier('style_sponsor') ? 135 : 180),
  );
  const riskScore =
    input.closestCopDistance < 7
      ? 16
      : input.closestCopDistance < 12
        ? 12
        : input.closestCopDistance < 22
          ? 8
          : 4;
  const craftScore =
    Math.min(10, input.nearMisses * 2.4) +
    Math.min(8, input.pursuitBreakers * 4) +
    Math.min(8, input.takedowns * 2.4);

  const completionBonus = input.completed ? 10 : -28;
  const modifierScore =
    (hasModifier('breaker_bonus') ? Math.min(8, input.pursuitBreakers * 8) : 0) -
    (hasModifier('clean_delivery') ? Math.min(12, (input.totalCollisions ?? 0) * 3) : 0);
  const raw =
    completionBonus +
    contractScore(contract, input) +
    speedScore +
    styleScore +
    riskScore +
    craftScore +
    modifierScore;
  const score = Math.max(0, Math.min(100, Math.round(raw)));

  const grade: RunGrade =
    score >= 92 ? 'SS' : score >= 82 ? 'S' : score >= 68 ? 'A' : score >= 50 ? 'B' : 'C';

  const label =
    grade === 'SS'
      ? 'Competition Clip'
      : grade === 'S'
        ? 'Elite Getaway'
        : grade === 'A'
          ? 'Clean Contract'
          : grade === 'B'
            ? 'Paid Run'
            : 'Messy Escape';

  const detail =
    grade === 'SS'
      ? 'This is the kind of run judges remember.'
      : grade === 'S'
        ? 'Fast, stylish, and dangerous enough to replay.'
        : grade === 'A'
          ? 'Strong contract finish with room for a bigger chain.'
          : grade === 'B'
            ? 'The route worked, but the highlight reel needs more risk.'
            : 'Win the contract cleaner, faster, and louder.';

  return { grade, score, label, detail };
}
